import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UniversalProduct from 'pages/UniversalProductsPage/ProductsPage/UniversalProduct';
import { CenteredSpin, Flex, MarginBox, WhiteButton } from 'UI';

const PAGING_SIZE = 15;

export interface ServerShowMoreProps {
  hasMore: boolean;
  loading: boolean;
  onShowMore: () => void;
}

export interface UniversalProductsCardsProps {
  displayReferences: string[];
  isDataStillLoading: boolean;
  pagingResetKey: string;
  serverShowMore?: ServerShowMoreProps;
}

export const UniversalProductsCards = ({
  displayReferences,
  isDataStillLoading,
  pagingResetKey,
  serverShowMore,
}: UniversalProductsCardsProps) => {
  const { t } = useTranslation();
  const [showCount, setShowCount] = useState(PAGING_SIZE);

  useEffect(() => {
    setShowCount(PAGING_SIZE);
  }, [pagingResetKey]);

  const usesServerPaging = Boolean(serverShowMore);

  const visibleReferences = useMemo(() => {
    if (usesServerPaging) {
      return displayReferences;
    }
    return displayReferences.slice(0, showCount);
  }, [displayReferences, usesServerPaging, showCount]);

  const hasMoreLocal = !usesServerPaging && displayReferences.length > visibleReferences.length;

  const handleShowMoreLocal = () => {
    setShowCount((prevState) => prevState + PAGING_SIZE);
  };

  const cards = (
    <Flex gap={30} wrap={'wrap'} justify={'space-around'} size={0}>
      {visibleReferences.map((ref) => (
        <UniversalProduct key={`universal-product-${ref}`} referenceNumber={ref} />
      ))}
    </Flex>
  );

  const showServerButton = serverShowMore?.hasMore || (serverShowMore?.loading ?? false);

  return (
    <>
      {cards}
      {showServerButton && (
        <Flex justify={'center'}>
          <MarginBox my={30}>
            <WhiteButton outlined disabled={serverShowMore?.loading} onClick={serverShowMore?.onShowMore}>
              {t('common.action.show_more', 'Show more')}
            </WhiteButton>
          </MarginBox>
        </Flex>
      )}
      {!serverShowMore && hasMoreLocal && (
        <Flex justify={'center'}>
          <MarginBox my={30}>
            <WhiteButton outlined onClick={handleShowMoreLocal}>
              {t('common.action.show_more', 'Show more')}
            </WhiteButton>
          </MarginBox>
        </Flex>
      )}
      {(isDataStillLoading || serverShowMore?.loading) && (
        <div data-testid="loading-spinner">
          <MarginBox mt={20}>
            <CenteredSpin />
          </MarginBox>
        </div>
      )}
    </>
  );
};
